import { ChangeDetectionStrategy, Component, computed, ViewEncapsulation } from '@angular/core';
import { PlotOptions, Point, SeriesPieOptions } from 'highcharts';
import { PbiChartTypes } from '../../../shared/chart/chart';
import { VisualFieldRoleType } from '../VisualFieldRoleType';
import { VisualFieldRoleTypeMap } from '../VisualFieldRoleTypeMap';
import { DataPoint, DataPointField, DataPointValues } from '../VisualInteractions';
import { FontSize } from '../VisualStyle';
import { HighchartsChart } from './base/PbirHighchartsChart.component';
import { ChartPoint, ChartSeries, ChartVisual, ChartVisualComponent, CreateSeriesOptions, CustomLegendMode, formatPercentage, SeriesField, TooltipCustomData, TooltipParentData, TooltipPointData } from './ChartVisual.component';
import { DataTooltip, DataTooltipColumn, DataTooltipHeader, DataTooltipRow } from '../tooltip/DataTooltipComponent';
import { VisualField, VisualFieldSortingInit } from '../VisualField';
import { SortDirection } from 'src/app/services/api.services';

@Component({
  selector: 'pbir-visual__pieChart',
  templateUrl: './ChartVisual.component.html',
  styleUrls: ['./ChartVisual.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class PieChartVisualComponent
  extends ChartVisualComponent<
    SeriesPieOptions,
    PieChartVisual.Options,
    PieChartVisual.State,
    PieChartVisual.Style> {
  get measuresRoleType() { return PieChartVisual.roles.measures; }
  get groupingRoleType() { return PieChartVisual.roles.legend; }
  get legendRoleType() { return PieChartVisual.roles.detail; }
  
  get renderRowLimit() { return 180; }

  readonly displayType = computed(() => this.options().displayType);
  
  readonly donut = computed(() => this.displayType() == PieChartVisual.DisplayType.Donut);
  readonly pie = computed(() => this.displayType() == PieChartVisual.DisplayType.Pie);
  
  get chartType() {
    return 'pie';
  }

  computePlotOptions(): PlotOptions {
    let dataLabels = this.style()?.dataLabels();
    let dataLabelsStyle = this.dataLabelsStyle();
    let dataLabelsFontSizeCss = FontSize.toCss(dataLabelsStyle?.fontSize, 12);

    return {
      ...super.computePlotOptions(),
      pie: {
        animation: !this.disableAnimations,

        innerSize: this.donut() ? '50%' : '0%',
        size: '75%',

        allowPointSelect: true,
        
        dataLabels: {
          ...this.createDataLabelsOptions(),
        },

        showInLegend: true,
      }
    };
  }


  createAllSeries(o: CreateSeriesOptions): ChartSeries<Highcharts.SeriesOptions>[] {
    let series: ChartSeries<Highcharts.SeriesOptions>[] = [];

    let groupings: SeriesField[] = [
      ...o.groupings,
      ...o.legends,
    ];

    let measures: SeriesField[] = [
      ...o.measures
    ];

    let grouping = groupings.length > 0 ? groupings[0] : null;
    let legend   = groupings.length > 1 ? groupings[1] : null;

    let seriesName = grouping?.field.shortTitle ?? 'Pie series';
    let seriesId = `_${ChartVisual.getNextSeriesId()}`;

    let data: ChartPoint[] = [];

    let s: ChartSeries<Highcharts.SeriesOptions> = {
      id: seriesId,
      name: seriesName,
      data: data,
      dataGrouping: { enabled: false },
      dataLabels: this.dataLabels(),
    };

    series.push(s);

    let groups = new Map<any, any[]>;

    if (grouping) {
      for (let row of o.result.rows) {
        let key = o.groupingData(0, row);
        let group = groups.get(key);
        if (!group) {
          group = [];
          groups.set(key, group);
        }

        group.push(row);
      }
    }
    else {
      groups.set(null, <any>o.result.rows);
    }

    let nextGroupingColorIndex = 0;

    for (let [_, rows] of groups) {
      let groupingColor = HighchartsChart.getColorByIndex(nextGroupingColorIndex++);
      
      for (let r of rows) {
        let groupingKey;
        let groupingDisplay: ReadonlyArray<string> = [];
        let groupingDisplayString = '';

        if (grouping) {
          groupingKey = o.groupingData(0, r);
          groupingDisplay = o.allGroupingsDisplay(r);
          groupingDisplayString = groupingDisplay.join(', ');
        }
        
        let dataPointFields: DataPointField[] = [];
        o.groupings?.forEach(g => dataPointFields.push({ field: g.field, value: r[g.dataIndex] }));
        o.legends  ?.forEach(g => dataPointFields.push({ field: g.field, value: r[g.dataIndex] }));

        let measureIndex = 0;

        for (let m of o.measures) {
          if (!grouping)
            groupingKey = measureIndex;
            
          let valueDisplay = o.formatter.getValueDisplay(m.field, r[m.dataIndex]);

          let parent: TooltipParentData = null;
          let value = o.measureData(m.index, r);
          let label;

          if (o.measures.length > 1) {
            label = m.field.shortTitle;
          }
          else if (legend) {
            label = o.legendDisplay(0, r);
            parent = {
              title: legend.field.shortTitle,
              display: label
            };
          }
          else {
            label = groupingDisplayString;
          }

          dataPointFields.push({ field: m.field, value });

          let dataPoint = this.dataPoints.get(dataPointFields);

          let color = grouping ? groupingColor : HighchartsChart.getColorByIndex(measureIndex++);
          
          let p: ChartPoint = {
            name: label,
            label,
            y: value,
            custom: this.createSeriesPointCustomData(
              dataPoint,
              groupingKey,
              grouping ? o.groupings : [],
              groupingDisplay,
              m.field,
              valueDisplay,
              parent),
            color: color
          };

          p.custom.shared = false;

          data.push(p);
        }
      }
    }

    return series;
  }

  useSingleSeriesStack() { return true; }
  computeShowPercentageOfTotalInDataLabel() { return true; }
  computeShowNameInDataLabel() { return true; }

  customLegendMode(): CustomLegendMode {
    return CustomLegendMode.PerGrouping;
  }

  tooltipsIncludePercentage() {
    return true;
  }

  override applyDefaultSorting(fields: VisualField[]) {

    let legendFieldIndex = fields.findIndex((f) => f.role().type.title == VisualFieldRoleType.Defaults.detailLegend.title);
    let detailsFieldIndex = fields.findIndex((f) => f.role().type.title == VisualFieldRoleType.Defaults.detailDetails.title);
    let index = detailsFieldIndex != -1 ? detailsFieldIndex : legendFieldIndex;
    if (index != -1) {
      let field = fields[index];

      fields[index] = new VisualField(
        field.target,
        field.visualFieldId,
        <VisualFieldSortingInit>{
          sorted: true,
          sortDirection: SortDirection.Ascending,
          sortOrder: 0
        }
      );

      return fields;
    }

    return fields;
  }
}

export namespace PieChartVisual {
  export enum DisplayType {
    Pie = 'pie',
    Donut = 'donut'
  }

  export class State extends ChartVisual.State { }
  export class Style extends ChartVisual.Style { }

  export interface Options extends ChartVisual.Options {
    readonly displayType: DisplayType;
  }

  export const roles = VisualFieldRoleTypeMap.create({
    legend: new VisualFieldRoleType('legend', {
      ...VisualFieldRoleType.Defaults.groupingLegend,
      limits: v => ({ min: 0, max: 1 })
    }),

    detail: new VisualFieldRoleType('detail', {
      ...VisualFieldRoleType.Defaults.detailDetails,
      isEnabled: v => v.fieldRoles.get(roles.measures).fields.length <= 1,
      limits: v => ({ min: 0, max: 1 })
    }),

    measures: new VisualFieldRoleType('measures', {
      ...VisualFieldRoleType.Defaults.measures,
      limits: v =>
        (v.fieldRoles.get(roles.detail).fields.length > 0)
          ? { min: 1, max: 1 }
          : { min: 1 }
    }),
  });

  export const type = ChartVisual.defineType<PieChartVisualComponent>(
    PieChartVisualComponent,
    'pieChart',
    roles,
    {
      visualOptions: {
        noXAxis: true,
        noYAxis: true,
        displayType: DisplayType.Pie,
        dataLabelPosition: ChartVisual.DataLabelsPosition.Outside,

        legendEnabledByDefault: true,
        dataLabelsEnabledByDefault: false,
      },
      createState: () => new State(),
      createStyle: () => new Style(),
      showOverlayUntilRendered: true,
      getPbiChartType: v => {
        let opt = v.options() as Options;
        if (opt.displayType == DisplayType.Donut) {
          return PbiChartTypes.donutChart;
        }
        else {
          return PbiChartTypes.pieChart;
        }
      },

      percentageDisplayChart: true,
    });
}
